<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import AutomationEventsAPI from 'dashboard/api/automationEvents';
import AutomationEventInspector from 'dashboard/components/widgets/automation/AutomationEventInspector.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const FILTER_KEYS = [
  'q',
  'email',
  'phone',
  'contact_id',
  'launch_id',
  'transaction_id',
  'source',
  'event_type',
  'product',
  'status',
  'from',
  'to',
];
const URL_OWNED_KEYS = [
  ...FILTER_KEYS,
  'errors',
  'advanced',
  'page',
  'selected',
];

const emptyFilters = () => ({
  q: '',
  email: '',
  phone: '',
  contact_id: '',
  launch_id: '',
  transaction_id: '',
  source: '',
  event_type: '',
  product: '',
  status: '',
  errors: false,
  from: '',
  to: '',
});

const filters = ref(emptyFilters());
const searchText = ref('');
const events = ref([]);
const meta = ref({
  page: 1,
  per_page: 50,
  total: 0,
  status_counts: {},
});
const isLoading = ref(false);
const error = ref('');
const partialError = ref('');
const selectedEvent = ref(null);
const selectedEventId = ref(null);
const isDetailLoading = ref(false);
const showAdvanced = ref(false);
const isNarrow = ref(false);

let activeFetchRequestId = 0;
let activeDetailRequestId = 0;
let searchTimer = null;
let syncTimer = null;
let mediaQuery = null;
let isHydratingFromRoute = false;
let skipNextUrlSync = false;

const statusOptions = [
  'received',
  'processing',
  'completed',
  'failed',
  'manual_action',
  'ignored',
];

const kpiDefs = [
  { key: 'total', status: '', labelKey: 'AUTOMATION_EVENTS.KPI.TOTAL' },
  {
    key: 'completed',
    status: 'completed',
    labelKey: 'AUTOMATION_EVENTS.KPI.COMPLETED',
  },
  {
    key: 'processing',
    status: 'processing',
    labelKey: 'AUTOMATION_EVENTS.KPI.PROCESSING',
  },
  { key: 'failed', status: 'failed', labelKey: 'AUTOMATION_EVENTS.KPI.FAILED' },
];

const hasNextPage = computed(
  () => meta.value.page * meta.value.per_page < meta.value.total
);
const hasPreviousPage = computed(() => meta.value.page > 1);

const sanitizedFilters = computed(() =>
  Object.entries(filters.value).reduce((params, [key, value]) => {
    if (value) params[key] = value;
    return params;
  }, {})
);

const statusCounts = computed(() => meta.value.status_counts || {});

const kpiValue = key => {
  if (key === 'total') {
    return statusCounts.value.total ?? meta.value.total ?? 0;
  }
  return statusCounts.value[key] ?? 0;
};

const formatShortDate = value => {
  if (!value) return t('AUTOMATION_EVENTS.EMPTY_VALUE');

  return new Date(value).toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusLabel = status => {
  switch (status) {
    case 'completed':
      return t('AUTOMATION_EVENTS.STATUSES.COMPLETED');
    case 'failed':
      return t('AUTOMATION_EVENTS.STATUSES.FAILED');
    case 'ignored':
      return t('AUTOMATION_EVENTS.STATUSES.IGNORED');
    case 'manual_action':
      return t('AUTOMATION_EVENTS.STATUSES.MANUAL_ACTION');
    case 'processing':
      return t('AUTOMATION_EVENTS.STATUSES.PROCESSING');
    case 'received':
      return t('AUTOMATION_EVENTS.STATUSES.RECEIVED');
    default:
      return status;
  }
};

const statusClass = status => {
  const classes = {
    completed: 'bg-n-teal-3 text-n-teal-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    manual_action: 'bg-n-amber-3 text-n-amber-11',
    processing: 'bg-n-blue-3 text-n-blue-11',
    ignored: 'bg-n-slate-3 text-n-slate-11',
    received: 'bg-n-slate-3 text-n-slate-11',
  };
  return classes[status] || classes.received;
};

const customerLabel = event =>
  event.customer_name ||
  event.customer_email ||
  event.customer_phone ||
  t('AUTOMATION_EVENTS.EMPTY_VALUE');

const activeChips = computed(() => {
  const chips = [];
  if (filters.value.q) {
    chips.push({
      key: 'q',
      label: t('AUTOMATION_EVENTS.CHIPS.Q', { value: filters.value.q }),
    });
  }
  if (filters.value.status) {
    chips.push({
      key: 'status',
      label: t('AUTOMATION_EVENTS.CHIPS.STATUS', {
        value: statusLabel(filters.value.status),
      }),
    });
  }
  if (filters.value.errors) {
    chips.push({
      key: 'errors',
      label: t('AUTOMATION_EVENTS.CHIPS.ERRORS'),
    });
  }
  const map = [
    ['email', 'EMAIL'],
    ['phone', 'PHONE'],
    ['contact_id', 'CONTACT_ID'],
    ['launch_id', 'LAUNCH'],
    ['transaction_id', 'TRANSACTION'],
    ['source', 'SOURCE'],
    ['event_type', 'EVENT_TYPE'],
    ['product', 'PRODUCT'],
    ['from', 'FROM'],
    ['to', 'TO'],
  ];
  map.forEach(([key, i18nKey]) => {
    if (filters.value[key]) {
      chips.push({
        key,
        label: t(`AUTOMATION_EVENTS.CHIPS.${i18nKey}`, {
          value: filters.value[key],
        }),
      });
    }
  });
  return chips;
});

const showMobileInspector = computed(
  () => isNarrow.value && Boolean(selectedEventId.value)
);

const normalizeQueryValue = value => {
  if (Array.isArray(value)) return value[0] == null ? '' : String(value[0]);
  if (value == null) return '';
  return String(value);
};

const queriesEqual = (left = {}, right = {}) => {
  const keys = new Set([
    ...Object.keys(left),
    ...Object.keys(right),
    ...URL_OWNED_KEYS,
  ]);
  return [...keys].every(
    key => normalizeQueryValue(left[key]) === normalizeQueryValue(right[key])
  );
};

const buildQueryFromState = () => {
  const query = {};
  FILTER_KEYS.forEach(key => {
    if (filters.value[key]) query[key] = String(filters.value[key]);
  });
  if (filters.value.errors) query.errors = '1';
  if (showAdvanced.value) query.advanced = '1';
  if (meta.value.page > 1) query.page = String(meta.value.page);
  if (selectedEventId.value) query.selected = String(selectedEventId.value);
  return query;
};

const hydrateFromQuery = (query = route.query || {}) => {
  isHydratingFromRoute = true;
  skipNextUrlSync = true;

  const next = emptyFilters();
  FILTER_KEYS.forEach(key => {
    const value = normalizeQueryValue(query[key]);
    if (value) next[key] = value;
  });
  const errorsValue = normalizeQueryValue(query.errors);
  next.errors = errorsValue === '1' || errorsValue === 'true';
  filters.value = next;
  searchText.value = next.q;
  showAdvanced.value = normalizeQueryValue(query.advanced) === '1';

  const page = parseInt(normalizeQueryValue(query.page), 10);
  meta.value.page = Number.isFinite(page) && page >= 1 ? page : 1;

  const selected = normalizeQueryValue(query.selected);
  selectedEventId.value = selected ? Number(selected) || selected : null;

  isHydratingFromRoute = false;
};

const syncStateToUrl = () => {
  if (isHydratingFromRoute || skipNextUrlSync) {
    skipNextUrlSync = false;
    return;
  }
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    if (isHydratingFromRoute) return;
    const nextQuery = buildQueryFromState();
    if (queriesEqual(route.query || {}, nextQuery)) return;
    router.replace({ query: nextQuery }).catch(() => {});
  }, 120);
};

const fetchEvents = async (page = meta.value.page) => {
  activeFetchRequestId += 1;
  const requestId = activeFetchRequestId;
  isLoading.value = true;
  error.value = '';
  try {
    const { data } = await AutomationEventsAPI.list({
      ...sanitizedFilters.value,
      page,
      per_page: meta.value.per_page,
    });
    if (requestId !== activeFetchRequestId) return;

    events.value = data?.data || [];
    meta.value = {
      page: data?.meta?.page || page,
      per_page: data?.meta?.per_page || meta.value.per_page,
      total: data?.meta?.total ?? events.value.length,
      status_counts: data?.meta?.status_counts || {},
    };

    if (
      selectedEventId.value &&
      !events.value.some(
        event => String(event.id) === String(selectedEventId.value)
      )
    ) {
      // Keep selection if not on this page; detail still loads by id.
    }
  } catch {
    if (requestId !== activeFetchRequestId) return;

    events.value = [];
    meta.value = {
      page,
      per_page: meta.value.per_page,
      total: 0,
      status_counts: {},
    };
    error.value = t('AUTOMATION_EVENTS.ERROR');
  } finally {
    if (requestId === activeFetchRequestId) isLoading.value = false;
  }
};

const loadDetail = async eventId => {
  if (!eventId) {
    selectedEvent.value = null;
    isDetailLoading.value = false;
    return;
  }

  activeDetailRequestId += 1;
  const requestId = activeDetailRequestId;
  isDetailLoading.value = true;
  partialError.value = '';

  const listHit = events.value.find(
    event => String(event.id) === String(eventId)
  );
  if (listHit) selectedEvent.value = listHit;

  try {
    const { data } = await AutomationEventsAPI.show(eventId);
    if (requestId !== activeDetailRequestId) return;

    selectedEvent.value = data?.automation_event || listHit || null;
  } catch {
    if (requestId !== activeDetailRequestId) return;

    if (listHit) {
      selectedEvent.value = listHit;
      partialError.value = t('AUTOMATION_EVENTS.PARTIAL_ERROR');
    } else {
      selectedEvent.value = null;
      selectedEventId.value = null;
      error.value = t('AUTOMATION_EVENTS.ERROR');
    }
  } finally {
    if (requestId === activeDetailRequestId) isDetailLoading.value = false;
  }
};

const selectEvent = event => {
  if (!event) {
    selectedEventId.value = null;
    selectedEvent.value = null;
    return;
  }
  selectedEventId.value = event.id;
};

const clearSelection = () => {
  selectedEventId.value = null;
  selectedEvent.value = null;
};

const applyKpiFilter = status => {
  filters.value.status = status;
  meta.value.page = 1;
  fetchEvents(1);
};

const setQuickStatus = status => {
  filters.value.status = filters.value.status === status ? '' : status;
  meta.value.page = 1;
  fetchEvents(1);
};

const removeChip = key => {
  if (key === 'errors') {
    filters.value.errors = false;
  } else if (key === 'q') {
    filters.value.q = '';
    searchText.value = '';
  } else {
    filters.value[key] = '';
  }
  meta.value.page = 1;
  fetchEvents(1);
};

const clearFilters = () => {
  filters.value = emptyFilters();
  searchText.value = '';
  meta.value.page = 1;
  fetchEvents(1);
};

const applyAdvancedFilters = () => {
  meta.value.page = 1;
  fetchEvents(1);
};

const onSearchInput = value => {
  searchText.value = value;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    filters.value.q = value.trim();
    meta.value.page = 1;
    fetchEvents(1);
  }, 300);
};

const goToPage = page => {
  meta.value.page = page;
  fetchEvents(page);
};

const updateMediaQuery = () => {
  isNarrow.value = Boolean(mediaQuery?.matches);
};

const invalidateInFlightRequests = () => {
  activeFetchRequestId += 1;
  activeDetailRequestId += 1;
};

// Single path for detail loads: selection changes only (no mount + watch double-call).
watch(selectedEventId, id => {
  if (id) {
    loadDetail(id);
    return;
  }
  selectedEvent.value = null;
  partialError.value = '';
  isDetailLoading.value = false;
});

watch(
  [filters, () => meta.value.page, selectedEventId, showAdvanced],
  () => syncStateToUrl(),
  { deep: true }
);

// Rehydrate when the browser rewrites the URL (back/forward or deep-link paste).
watch(
  () => route.query,
  query => {
    const nextQuery = {};
    URL_OWNED_KEYS.forEach(key => {
      const value = normalizeQueryValue(query?.[key]);
      if (value) nextQuery[key] = value;
    });
    if (queriesEqual(nextQuery, buildQueryFromState())) return;

    hydrateFromQuery(query || {});
    fetchEvents(meta.value.page);
  },
  { deep: true }
);

onMounted(() => {
  hydrateFromQuery();
  mediaQuery = window.matchMedia('(max-width: 1023px)');
  updateMediaQuery();
  mediaQuery.addEventListener('change', updateMediaQuery);
  fetchEvents(meta.value.page);
  // Detail loads only via the selectedEventId watcher (hydrate may set it once).
});

onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer);
  if (syncTimer) clearTimeout(syncTimer);
  searchTimer = null;
  syncTimer = null;
  if (mediaQuery) {
    mediaQuery.removeEventListener('change', updateMediaQuery);
    mediaQuery = null;
  }
  invalidateInFlightRequests();
});
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-hidden bg-n-surface-1">
    <header class="shrink-0 border-b border-n-weak px-4 py-3 lg:px-6 lg:py-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="min-w-0">
          <h1 class="text-xl font-extrabold text-n-slate-12">
            {{ t('AUTOMATION_EVENTS.PAGE.TITLE') }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('AUTOMATION_EVENTS.PAGE.DESCRIPTION') }}
          </p>
        </div>
        <button
          type="button"
          class="inline-flex h-9 items-center gap-2 rounded-md bg-n-alpha-2 px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-3"
          @click="fetchEvents(meta.page)"
        >
          <span class="i-ri-refresh-line size-4" />
          {{ t('AUTOMATION_EVENTS.ACTIONS.REFRESH') }}
        </button>
      </div>
    </header>

    <section
      class="shrink-0 space-y-3 border-b border-n-weak px-4 py-3 lg:px-6"
    >
      <div class="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <button
          v-for="kpi in kpiDefs"
          :key="kpi.key"
          type="button"
          class="rounded-xl border px-3 py-2 text-left transition-colors"
          :class="
            filters.status === kpi.status
              ? 'border-n-brand bg-n-brand/10'
              : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-1'
          "
          @click="applyKpiFilter(kpi.status)"
        >
          <div
            class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
          >
            {{ t(kpi.labelKey) }}
          </div>
          <div
            class="mt-0.5 text-xl font-extrabold tabular-nums text-n-slate-12"
          >
            {{ kpiValue(kpi.key) }}
          </div>
        </button>
      </div>

      <div class="flex flex-col gap-2 lg:flex-row lg:items-center">
        <div class="relative min-w-0 flex-1">
          <span
            class="i-ri-search-line pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
          />
          <input
            :value="searchText"
            type="search"
            class="h-9 w-full rounded-md border border-n-weak bg-n-solid-1 py-2 pl-9 pr-3 text-sm text-n-slate-12"
            :placeholder="t('AUTOMATION_EVENTS.FILTERS.SEARCH')"
            @input="onSearchInput($event.target.value)"
          />
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-xs font-medium text-n-slate-10">
            {{ t('AUTOMATION_EVENTS.FILTERS.QUICK_STATUS') }}
          </span>
          <button
            v-for="status in ['completed', 'processing', 'failed']"
            :key="status"
            type="button"
            class="inline-flex h-8 items-center rounded-full px-2.5 text-xs font-medium"
            :class="
              filters.status === status
                ? statusClass(status)
                : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
            "
            @click="setQuickStatus(status)"
          >
            {{ statusLabel(status) }}
          </button>
          <label
            class="inline-flex h-8 items-center gap-1.5 rounded-full bg-n-alpha-2 px-2.5 text-xs font-medium text-n-slate-11"
          >
            <input
              v-model="filters.errors"
              type="checkbox"
              class="size-3.5"
              @change="applyAdvancedFilters"
            />
            {{ t('AUTOMATION_EVENTS.FILTERS.ERRORS') }}
          </label>
          <button
            type="button"
            class="inline-flex h-8 items-center gap-1 rounded-md bg-n-alpha-2 px-2.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-3"
            @click="showAdvanced = !showAdvanced"
          >
            <span class="i-ri-equalizer-line size-3.5" />
            {{
              showAdvanced
                ? t('AUTOMATION_EVENTS.ACTIONS.HIDE_ADVANCED')
                : t('AUTOMATION_EVENTS.ACTIONS.SHOW_ADVANCED')
            }}
          </button>
        </div>
      </div>

      <div
        v-if="showAdvanced"
        class="rounded-xl border border-n-weak bg-n-alpha-1 p-3"
      >
        <div class="grid gap-2 md:grid-cols-3 xl:grid-cols-5">
          <input
            v-model="filters.email"
            type="email"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :placeholder="t('AUTOMATION_EVENTS.FILTERS.EMAIL')"
          />
          <input
            v-model="filters.phone"
            type="tel"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :placeholder="t('AUTOMATION_EVENTS.FILTERS.PHONE')"
          />
          <input
            v-model="filters.contact_id"
            type="number"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :placeholder="t('AUTOMATION_EVENTS.FILTERS.CONTACT_ID')"
          />
          <input
            v-model="filters.launch_id"
            type="text"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :placeholder="t('AUTOMATION_EVENTS.FILTERS.LAUNCH')"
          />
          <input
            v-model="filters.transaction_id"
            type="text"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :placeholder="t('AUTOMATION_EVENTS.FILTERS.TRANSACTION')"
          />
          <input
            v-model="filters.source"
            type="text"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :placeholder="t('AUTOMATION_EVENTS.FILTERS.SOURCE')"
          />
          <input
            v-model="filters.event_type"
            type="text"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :placeholder="t('AUTOMATION_EVENTS.FILTERS.EVENT_TYPE')"
          />
          <input
            v-model="filters.product"
            type="text"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :placeholder="t('AUTOMATION_EVENTS.FILTERS.PRODUCT')"
          />
          <select
            v-model="filters.status"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
          >
            <option value="">
              {{ t('AUTOMATION_EVENTS.FILTERS.STATUS') }}
            </option>
            <option
              v-for="status in statusOptions"
              :key="status"
              :value="status"
            >
              {{ statusLabel(status) }}
            </option>
          </select>
          <input
            v-model="filters.from"
            type="datetime-local"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :aria-label="t('AUTOMATION_EVENTS.FILTERS.FROM')"
          />
          <input
            v-model="filters.to"
            type="datetime-local"
            class="h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :aria-label="t('AUTOMATION_EVENTS.FILTERS.TO')"
          />
          <div class="flex gap-2 md:col-span-2 xl:col-span-1">
            <button
              type="button"
              class="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white hover:opacity-90"
              @click="applyAdvancedFilters"
            >
              <span class="i-ri-search-line size-4" />
              {{ t('AUTOMATION_EVENTS.ACTIONS.APPLY') }}
            </button>
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center rounded-md bg-n-alpha-2 px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-3"
              @click="clearFilters"
            >
              {{ t('AUTOMATION_EVENTS.ACTIONS.CLEAR') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="activeChips.length" class="flex flex-wrap gap-1.5">
        <button
          v-for="chip in activeChips"
          :key="chip.key"
          type="button"
          class="inline-flex h-7 items-center gap-1 rounded-full border border-n-weak bg-n-solid-1 px-2.5 text-xs text-n-slate-12 hover:bg-n-alpha-2"
          :aria-label="t('AUTOMATION_EVENTS.ACTIONS.REMOVE_FILTER')"
          @click="removeChip(chip.key)"
        >
          <span class="max-w-[14rem] truncate">{{ chip.label }}</span>
          <span class="i-ri-close-line size-3 text-n-slate-10" />
        </button>
      </div>
    </section>

    <div
      v-if="error"
      class="shrink-0 border-b border-n-ruby-5 bg-n-ruby-2 px-4 py-2 text-sm text-n-ruby-11 lg:px-6"
    >
      {{ error }}
    </div>
    <div
      v-else-if="partialError"
      class="shrink-0 border-b border-n-amber-5 bg-n-amber-2 px-4 py-2 text-sm text-n-amber-11 lg:px-6"
    >
      {{ partialError }}
    </div>

    <div class="flex min-h-0 flex-1 overflow-hidden">
      <section
        class="flex min-h-0 min-w-0 flex-col border-n-weak lg:w-[48%] lg:border-r xl:w-[52%]"
        :class="{ hidden: showMobileInspector }"
      >
        <div
          class="flex shrink-0 items-center justify-between border-b border-n-weak px-4 py-2"
        >
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ t('AUTOMATION_EVENTS.PAGE.LIST_TITLE') }}
          </h2>
          <span class="text-xs text-n-slate-11">
            {{ t('AUTOMATION_EVENTS.PAGE.TOTAL', { total: meta.total }) }}
          </span>
        </div>

        <div class="min-h-0 flex-1 overflow-auto">
          <div
            v-if="isLoading"
            class="flex items-center justify-center gap-2 py-16 text-sm text-n-slate-11"
          >
            <span class="i-ri-loader-2-line size-4 animate-spin" />
            {{ t('AUTOMATION_EVENTS.LOADING') }}
          </div>

          <div
            v-else-if="!error && !events.length"
            class="px-4 py-16 text-center text-sm text-n-slate-11"
          >
            {{ t('AUTOMATION_EVENTS.PAGE.EMPTY') }}
          </div>

          <table
            v-else-if="!error"
            class="min-w-full divide-y divide-n-weak text-left"
          >
            <thead class="sticky top-0 z-10 bg-n-alpha-1">
              <tr>
                <th class="px-3 py-2 text-xs font-medium text-n-slate-11">
                  {{ t('AUTOMATION_EVENTS.FIELDS.RECEIVED_AT') }}
                </th>
                <th class="px-3 py-2 text-xs font-medium text-n-slate-11">
                  {{ t('AUTOMATION_EVENTS.FIELDS.CUSTOMER') }}
                </th>
                <th
                  class="hidden px-3 py-2 text-xs font-medium text-n-slate-11 md:table-cell"
                >
                  {{ t('AUTOMATION_EVENTS.FIELDS.EVENT_TYPE') }}
                </th>
                <th class="px-3 py-2 text-xs font-medium text-n-slate-11">
                  {{ t('AUTOMATION_EVENTS.FIELDS.STATUS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak bg-n-solid-1">
              <tr
                v-for="event in events"
                :key="event.id"
                class="cursor-pointer transition-colors"
                :class="
                  String(selectedEventId) === String(event.id)
                    ? 'bg-n-brand/10'
                    : 'hover:bg-n-alpha-1'
                "
                @click="selectEvent(event)"
              >
                <td class="whitespace-nowrap px-3 py-2 text-xs text-n-slate-11">
                  {{ formatShortDate(event.received_at) }}
                </td>
                <td class="max-w-[10rem] px-3 py-2">
                  <div class="truncate text-sm font-medium text-n-slate-12">
                    {{ customerLabel(event) }}
                  </div>
                  <div class="truncate text-xs text-n-slate-10">
                    {{ event.transaction_id || event.launch_id || '' }}
                  </div>
                </td>
                <td class="hidden max-w-[9rem] px-3 py-2 md:table-cell">
                  <div class="truncate text-sm text-n-slate-12">
                    {{ event.event_type }}
                  </div>
                  <div class="truncate text-xs text-n-slate-10">
                    {{ event.source }}
                  </div>
                </td>
                <td class="px-3 py-2">
                  <span
                    class="rounded px-2 py-0.5 text-xs font-medium"
                    :class="statusClass(event.status)"
                  >
                    {{ statusLabel(event.status) }}
                  </span>
                  <div
                    v-if="event.current_error"
                    class="mt-0.5 max-w-[8rem] truncate text-[11px] text-n-ruby-11"
                  >
                    {{ event.current_error }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer
          v-if="!error"
          class="flex shrink-0 items-center justify-between border-t border-n-weak px-3 py-2 text-sm text-n-slate-11"
        >
          <button
            type="button"
            class="inline-flex h-8 items-center gap-1 rounded-md bg-n-alpha-2 px-3 text-sm font-medium text-n-slate-12 disabled:opacity-50"
            :disabled="!hasPreviousPage"
            @click="goToPage(meta.page - 1)"
          >
            <span class="i-ri-arrow-left-s-line size-4" />
            {{ t('AUTOMATION_EVENTS.ACTIONS.PREVIOUS') }}
          </button>
          <span class="tabular-nums">{{ meta.page }}</span>
          <button
            type="button"
            class="inline-flex h-8 items-center gap-1 rounded-md bg-n-alpha-2 px-3 text-sm font-medium text-n-slate-12 disabled:opacity-50"
            :disabled="!hasNextPage"
            @click="goToPage(meta.page + 1)"
          >
            {{ t('AUTOMATION_EVENTS.ACTIONS.NEXT') }}
            <span class="i-ri-arrow-right-s-line size-4" />
          </button>
        </footer>
      </section>

      <aside
        class="hidden min-h-0 min-w-0 flex-1 lg:flex"
        data-testid="automation-event-inspector-desktop"
      >
        <AutomationEventInspector
          :event="selectedEvent"
          :is-loading="isDetailLoading"
        />
      </aside>

      <div
        v-if="showMobileInspector"
        class="fixed inset-0 z-40 flex flex-col bg-n-surface-1 lg:hidden"
        data-testid="automation-event-inspector-mobile"
      >
        <div
          class="flex shrink-0 items-center gap-2 border-b border-n-weak px-3 py-2"
        >
          <button
            type="button"
            class="inline-flex h-8 items-center gap-1 rounded-md bg-n-alpha-2 px-2.5 text-sm font-medium text-n-slate-12"
            @click="clearSelection"
          >
            <span class="i-ri-arrow-left-line size-4" />
            {{ t('AUTOMATION_EVENTS.ACTIONS.BACK_TO_LIST') }}
          </button>
        </div>
        <AutomationEventInspector
          class="min-h-0 flex-1"
          :event="selectedEvent"
          :is-loading="isDetailLoading"
          compact
          @close="clearSelection"
        />
      </div>
    </div>
  </div>
</template>
